import { gql, useQuery } from "@apollo/client";
import { CPagination } from "@coreui/react";
import {
  AddIcon,
  Avatar,
  Button,
  ExportIcon,
  FilterIcon,
  Menu,
  Popover,
  Position,
  Spinner,
  Table,
  TextDropdownButton,
  toaster,
} from "evergreen-ui";
import React, { useEffect, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import AdminAgentRow from "src/Components/AdminAgentRow";
import Refresh from "src/Components/Refresh";
import { USER_FRAGMENTS } from "src/fragments";
import useInput from "src/Hooks/useInput";
import { exportToCsv, getDateWithTime } from "src/utilities";
import styled from "styled-components";

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  padding: 1rem;
  flex-direction: column;
  padding-bottom: 20vh;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 1rem;
  @media (max-width: ${(props) => props.theme.maxWidth}) {
    flex-direction: column;
    justify-content: space-around;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: inherit;
  width: 100%;
  justify-content: flex-end;
  @media (max-width: ${(props) => props.theme.maxWidth}) {
    align-items: center;
  }
`;
const Title = styled.h1`
  ${(props) => props.theme.title};
  width: 100%;
  text-align: left;
`;

const LoadingContainer = styled.div`
  ${(props) => props.theme.loadingContainer}
`;

const LIST_AGENTS = gql`
  query getAllUsers($where: users_bool_exp, $offset: Int, $limit: Int) {
    users(where: $where, order_by: { created_at: desc }) {
      ...UserDetails
    }
    users_aggregate(where: $where) {
      aggregate {
        count(columns: id)
      }
    }
    paginated: users(
      where: $where
      order_by: { created_at: desc }
      offset: $offset
      limit: $limit
    ) {
      ...UserDetails
    }
    packages {
      id
      name
      chinese_name
      package_country {
        id
        name
      }
    }
  }
  ${USER_FRAGMENTS}
`;

interface IProps extends RouteComponentProps<any> {}

const AdminAgents: React.SFC<IProps> = ({ location, history, match }) => {
  let currentPage = location.search.includes("page=")
    ? parseInt(location.search.split("=")[1]) || 1
    : 1;

  currentPage = currentPage < 1 ? 1 : currentPage;
  let limit = 10;

  const menuOptions = [
    {
      label: "PHONE",
      value: "phone",
    },
    {
      label: "STOCK",
      value: "stock_balance",
    },
    {
      label: "EMAIL",
      value: "email",
    },
    {
      label: "UPLINE",
      value: "upline",
    },
    {
      label: "DOWNLINES",
      value: "downlines",
    },
  ];

  let searchUsername = match.params?.uid || "";
  const [currentSearchName, setCurrentSearchName] = useState(searchUsername);
  const searchInput = useInput(searchUsername);
  const [selectedPackage, setSelectedPackage] = useState<any>("all");
  const [page, setPage] = useState(currentPage);
  const [showColumn, setShowColumn] = useState("upline");
  const [isLoading, setIsLoading] = useState(false);
  let searchQuery = searchInput.value;
  const { data, loading, error, refetch } = useQuery(LIST_AGENTS, {
    variables: {
      offset: (page - 1) * limit < 0 ? 0 : (page - 1) * limit,
      limit,
      where: {
        _and: [
          {
            current_rank:
              selectedPackage === "all"
                ? {}
                : selectedPackage === null
                ? {
                    _is_null: true,
                  }
                : {
                    _eq: selectedPackage,
                  },
          },
          searchQuery === ""
            ? {}
            : {
                _or: [
                  {
                    username: { _ilike: searchQuery },
                  },
                  {
                    name: { _ilike: searchQuery },
                  },
                  {
                    email: { _ilike: searchQuery },
                  },
                  {
                    ic_no: { _ilike: searchQuery },
                  },
                  {
                    phone: { _ilike: searchQuery },
                  },
                  {
                    id: {
                      _eq: searchQuery,
                    },
                  },
                ],
              },
        ],
      },
    },
  });

  const refresh = () => {
    setIsLoading(true);
    refetch().then(() => {
      setIsLoading(false);
    });
  };

  const exportData = (e) => {
    e.preventDefault();
    let csv =
      "ID,NAME,USERNAME,IC,JOIN DATE,UPLINE,RANK,EMAIL,PHONE,DIRECT DOWNLINES,STOCK BALANCE\n";
    data.users.map((user) => {
      return (csv =
        csv +
        `${user.id},${user.name},${user.username},${
          user.ic_no
        },${getDateWithTime(user.created_at)},${
          user.users_upline ? user.users_upline.name : "NO UPLINE"
        } (${user.users_upline ? user.users_upline.username : "NO UPLINE"}),${
          user.user_rank ? user.user_rank.name : "NO RANK YET"
        },${user.email}, ${user.phone}, ${user.downlines},${
          user.stock_balance
        }\n`);
    });

    exportToCsv(csv, "MY_AGENTS");
    toaster.success("Data exported!");
  };
  useEffect(() => {
    currentPage !== page && setPage(currentPage);

    if (currentSearchName !== searchUsername) {
      setCurrentSearchName(searchUsername);
      searchInput.onChange(searchUsername);
    }
    if (error) {
      toaster.danger(error.message);
    }
  }, [
    error,
    currentPage,
    page,
    currentSearchName,
    searchInput,
    searchUsername,
  ]);

  const pageChange = (newPage) => {
    currentPage !== newPage && history.push(`/admin/agents/?page=${newPage}`);
  };

  const toggleShowNewOrder = () => {
    toaster.success("COMING SOON!");
  };

  return (
    <Container>
      <Header>
        <Title className=" flex flex-row items-center">
          Agents ({(!loading && data && data.users && data.users.length) || 0})
          <span>
            <Refresh onClick={refresh} loading={isLoading} />
          </span>
        </Title>
        <ButtonGroup>
          <Button
            onClick={toggleShowNewOrder}
            appearance={"minimal"}
            iconBefore={AddIcon}
          >
            New Agent
          </Button>
          <Button
            onClick={() => {
              toaster.warning("Coming soon!");
            }}
            appearance={"minimal"}
            iconBefore={FilterIcon}
          >
            Advanced Filter
          </Button>
          <Button
            onClick={exportData}
            appearance={"minimal"}
            iconBefore={ExportIcon}
          >
            Export to Excel
          </Button>
        </ButtonGroup>
      </Header>
      <Table
        marginBottom={"1rem"}
        maxHeight={"90vh"}
        width="100%"
        maxWidth="calc(100vw - 2rem)"
        overflow="scroll"
      >
        <Table.Head minWidth="1080px" accountForScrollbar={true}>
          <Table.SearchHeaderCell
            {...searchInput}
            placeholder="Search Name, username, email..."
          />
          <Table.TextHeaderCell>USERNAME</Table.TextHeaderCell>
          <Table.TextHeaderCell>IC NO</Table.TextHeaderCell>
          <Table.TextHeaderCell>JOIN DATE</Table.TextHeaderCell>
          <Table.TextHeaderCell>
            <Popover
              position={Position.BOTTOM_LEFT}
              content={({ close }) => (
                <Menu>
                  <Menu.OptionsGroup
                    title="Show"
                    options={menuOptions}
                    selected={showColumn}
                    onChange={(value) => {
                      setShowColumn(value);
                      close();
                    }}
                  />
                </Menu>
              )}
            >
              <TextDropdownButton>
                {showColumn.toUpperCase()}
              </TextDropdownButton>
            </Popover>
          </Table.TextHeaderCell>
          <Table.TextHeaderCell>
            <Popover
              content={({ close }) => (
                <Menu>
                  <Menu.Group title="Package">
                    {!loading && data ? (
                      <>
                        <Menu.Item
                          onSelect={() => {
                            setSelectedPackage("all");
                          }}
                        >
                          All
                        </Menu.Item>
                        <Menu.Item
                          onSelect={() => {
                            setSelectedPackage(null);
                          }}
                        >
                          No Rank
                        </Menu.Item>
                        {data.packages.map((pac, index) => (
                          <Menu.Item
                            onSelect={() => {
                              setSelectedPackage(pac.id);
                            }}
                            key={index}
                          >
                            {pac.chinese_name} | {pac.name} (
                            {pac.package_country.name})
                          </Menu.Item>
                        ))}
                      </>
                    ) : (
                      <Menu.Item
                        icon={Spinner}
                        onSelect={() => {
                          setSelectedPackage("all");
                        }}
                      >
                        Loading...
                      </Menu.Item>
                    )}
                  </Menu.Group>
                </Menu>
              )}
            >
              <TextDropdownButton>
                {selectedPackage === "all"
                  ? "RANK"
                  : selectedPackage === null
                  ? "NO RANK"
                  : data &&
                    data.packages &&
                    data.packages[
                      data.packages.findIndex((a) => a.id === selectedPackage)
                    ].name}
              </TextDropdownButton>
            </Popover>
          </Table.TextHeaderCell>
          <Table.TextHeaderCell></Table.TextHeaderCell>
        </Table.Head>
        <Table.Body
          marginBottom={"1rem"}
          maxHeight={"90vh"}
          minWidth="1080px"
          overflow="scroll"
        >
          {loading && (
            <LoadingContainer>
              <Spinner />
            </LoadingContainer>
          )}
          {!loading && error && error.message}
          {!loading &&
            data &&
            data.paginated &&
            data.paginated.map((user, index) => {
              let agent = {
                id: user.id,
                address: user.user_addresses,
                name: user.name,
                banks: user.user_banks,
                created_at: getDateWithTime(user.created_at),
                username: user.username,
                email: user.email,
                avatar: user.avatar,
                phone: user.phone,
                ic_no: user.ic_no,
                stock_balance: user.stock_balance,
                downlines: user.downlines,
                userRank: user.user_rank || "No rank",
                userUpline: user.users_upline || "No upline",
                upline: user.users_upline ? (
                  <span
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Avatar
                      src={user.users_upline.avatar}
                      name={user.users_upline.name}
                      size={24}
                      marginRight={".2rem"}
                    />
                    {user.users_upline.name}
                  </span>
                ) : (
                  "No upline"
                ),
                displayName: (
                  <span
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Avatar
                      src={user.avatar}
                      name={user.name}
                      size={24}
                      marginRight={".2rem"}
                    />
                    {user.name}
                  </span>
                ),
              };

              return (
                <AdminAgentRow
                  key={agent.id}
                  {...agent}
                  refetch={refetch}
                  showAgent={
                    currentSearchName === user.username &&
                    searchInput.value === user.username
                  }
                  showColumn={agent[showColumn]}
                />
              );
            })}
        </Table.Body>
      </Table>
      {!loading && data && data.users_aggregate && (
        <CPagination
          pages={Math.ceil(data.users_aggregate.aggregate.count / limit)}
          activePage={page}
          limit={5}
          dots={true}
          onActivePageChange={pageChange}
        />
      )}
    </Container>
  );
};

export default withRouter(AdminAgents);
